var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { jsx } from "react/jsx-runtime";
import * as Apollo from "@apollo/client";
import { gql, ApolloError, useApolloClient } from "@apollo/client";
import { createContext, useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router";
import { memoize, debounce } from "lodash";
const defaultOptions = {};
const CartFieldsFragmentDoc = gql`
    fragment CartFields on Cart {
  id
  items {
    product {
      id
      ... on AdProductAddOnCartItem {
        id
        job {
          id
          revision
        }
      }
      ... on AdProductCartItem {
        id
        job {
          id
          revision
        }
      }
      ... on AdProductUpgradeCartItem {
        id
        job {
          id
          revision
        }
      }
      ... on AdBudgetCartItem {
        id
      }
    }
    referenceId
    parentReferenceId
  }
  customerId
  productSetId
  promoCode
  isOrderReady
}
    `;
const AllowanceFieldsFragmentDoc = gql`
    fragment AllowanceFields on AdSpendFeature {
  allowance {
    value
    currency
  }
}
    `;
const DiscountsFieldsFragmentDoc = gql`
    fragment DiscountsFields on AdTypeDiscountsFeature {
  discounts {
    adType
    discount {
      discountPercentage
      discountAmount {
        value
        currency
      }
    }
  }
}
    `;
const AddItemToCartDocument = gql`
    mutation AddItemToCart($input: AddItemToCartInput!) {
  addItemToCart(input: $input) {
    ... on AddItemToCartSuccessPayload {
      cart {
        ...CartFields
      }
    }
    ... on AddItemToCartErrorPayload {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
const CreateOrderWithPromotionDocument = gql`
    mutation CreateOrderWithPromotion($input: CreateOrderInput!) {
  createOrderWithPromotion(input: $input) {
    ... on PromotionInvalidError {
      message
    }
    ... on CreateOrderSuccessPayload {
      order {
        orderId
        orderNumber
      }
    }
  }
}
    `;
const GetCartDocument = gql`
    query GetCart($cartId: ID!) {
  cart(cartId: $cartId) {
    ...CartFields
  }
}
    ${CartFieldsFragmentDoc}`;
function useGetCartQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(GetCartDocument, options);
}
const RefreshCartProductSetDocument = gql`
    mutation RefreshCartProductSet($input: UpdateCartProductSetInput!) {
  refreshCartProductSet(input: $input) {
    ... on RefreshCartProductSetSuccessPayload {
      cart {
        ...CartFields
      }
      hasPriceChanged
      hasCartItemsBeenRemoved
    }
    ... on ProductSetNotFoundError {
      message
    }
    ... on CartNotFoundError {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
const RemoveCartItemDocument = gql`
    mutation RemoveCartItem($input: RemoveCartItemInput!) {
  removeCartItem(input: $input) {
    ... on RemoveCartItemSuccessPayload {
      cart {
        ...CartFields
      }
    }
    ... on CartNotFoundError {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
const RemoveCartPromoCodeDocument = gql`
    mutation RemoveCartPromoCode($input: RemoveCartPromoCodeInput!) {
  removeCartPromoCode(input: $input) {
    ... on RemoveCartPromoCodeSuccessPayload {
      cart {
        ...CartFields
      }
    }
    ... on CartNotFoundError {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
const UpdateCartProductSetDocument = gql`
    mutation UpdateCartProductSet($input: UpdateCartProductSetInput!) {
  updateCartProductSet(input: $input) {
    ... on UpdateCartProductSetSuccessPayload {
      cart {
        ...CartFields
      }
    }
    ... on UpdateCartProductSetErrorPayload {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
const UpdateCartPromoCodeDocument = gql`
    mutation UpdateCartPromoCode($input: UpdateCartPromoCodeInput!) {
  updateCartPromoCode(input: $input) {
    ... on UpdateCartPromoCodeSuccessPayload {
      cart {
        ...CartFields
      }
    }
    ... on UpdateCartPromoCodeErrorPayload {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
const UpdateJobForCartItemByReferenceDocument = gql`
    mutation UpdateJobForCartItemByReference($input: UpdateJobForCartItemByReferenceInput!) {
  updateJobForCartItemByReference(input: $input) {
    ... on UpdateJobForCartItemByReferenceSuccessPayload {
      cart {
        ...CartFields
      }
    }
    ... on CartNotFoundError {
      message
    }
  }
}
    ${CartFieldsFragmentDoc}`;
const CreateProductSetForAdPostingDocument = gql`
    mutation CreateProductSetForAdPosting($input: CreateProductSetForAdPostingInput!) {
  createProductSetForAdPosting(input: $input) {
    productSet {
      id
    }
  }
}
    `;
const CreateProductSetForAdUpgradePurchaseDocument = gql`
    mutation CreateProductSetForAdUpgradePurchase($input: CreateProductSetForAdUpgradePurchaseInput!) {
  createProductSetForAdUpgradePurchase(input: $input) {
    productSet {
      id
    }
  }
}
    `;
const CreateProductSetForMultiLocationDocument = gql`
    mutation CreateProductSetForMultiLocation($input: CreateProductSetForMultiLocationInput!) {
  createProductSetForMultiLocation(input: $input) {
    productSet {
      id
    }
  }
}
    `;
const GetProductSetItemsWithFeaturesOnlyDocument = gql`
    query GetProductSetItemsWithFeaturesOnly($input: GetProductSetInput!) {
  getProductSet(input: $input) {
    ... on ProductSet {
      items {
        id
        features {
          id
          name
          type
          ... on BrandingFeature {
            name
            type
            hasLogo
            hasCoverImage
          }
          ... on DurationFeature {
            id
            name
            type
            value
            durationUnit: unit
          }
          ... on PerApplicantBillingFeature {
            name
            type
          }
          ... on RoleRequirementsMandatoryQuestionsFeature {
            name
            type
            questionAnswerSet
          }
          ... on BulletPointsFeature {
            name
            type
            count
          }
          ... on PerUnitBillingFeature {
            unit
            name
            type
          }
          ... on SponsoredSlotFeature {
            id
            name
            type
          }
          ... on AdSpendFeature {
            id
            name
            type
            ...AllowanceFields
          }
          ... on AdTypeDiscountsFeature {
            id
            name
            type
            ...DiscountsFields
          }
          ... on BrandedFeaturesFeature {
            id
            name
            type
          }
          ... on JobAdEditFeature {
            id
            name
            type
          }
          ... on StyleEnhancementFeature {
            id
            name
            type
            style
          }
          ... on DisplayTagFeature {
            id
            name
            type
            tag
          }
        }
      }
    }
  }
}
    ${AllowanceFieldsFragmentDoc}
${DiscountsFieldsFragmentDoc}`;
const PRICE_CHANGED_PREFIX = "priceChanged-";
const isServerRendering = () => typeof window === "undefined";
const getPriceChangedItemKey = (cartId) => `${PRICE_CHANGED_PREFIX}${cartId}`;
const setItemWithExpiry = (key, value, ttl) => {
  if (isServerRendering()) return;
  const item = {
    value,
    expiry: ttl.getTime()
  };
  localStorage.setItem(key, JSON.stringify(item));
};
const getItem = (key) => {
  if (isServerRendering()) return null;
  const itemStr = localStorage.getItem(key);
  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr);
  return item.value;
};
const deleteIfExpired = (key) => {
  if (isServerRendering()) return;
  const itemStr = localStorage.getItem(key);
  if (!itemStr) {
    return;
  }
  const item = JSON.parse(itemStr);
  if (Date.now() > item.expiry) {
    localStorage.removeItem(key);
  }
};
const setPriceChangedFlagInLocalStorage = (cartId, logger) => {
  if (isServerRendering()) return;
  try {
    const key = getPriceChangedItemKey(cartId);
    const twoMonthsFromNow = /* @__PURE__ */ new Date();
    twoMonthsFromNow.setMonth(twoMonthsFromNow.getMonth() + 2);
    setItemWithExpiry(key, "true", twoMonthsFromNow);
  } catch (err) {
    logger.error("Failed to set price changed flag in local storage", {
      err,
      activity: "setPriceChangedFlagInLocalStorage",
      module: "LocalStorage"
    });
  }
};
const getPriceChangedFlagFromLocalStorage = (cartId, logger) => {
  if (isServerRendering()) return false;
  try {
    const key = getPriceChangedItemKey(cartId);
    return getItem(key) === "true";
  } catch (error) {
    logger.error("Failed to get price changed flag in local storage", {
      error,
      activity: "getPriceChangedFlagFromLocalStorage",
      module: "LocalStorage"
    });
    return false;
  }
};
const cleanUpExpiredLocalStorageItems = (logger) => {
  if (isServerRendering()) return;
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.includes(PRICE_CHANGED_PREFIX)) {
        deleteIfExpired(key);
      }
    });
  } catch (error) {
    logger.error("Failed to delete price changed flags in local storage", {
      error,
      activity: "cleanUpExpiredLocalStorageItems",
      module: "LocalStorage"
    });
  }
};
const createCart = ({
  apolloClient,
  logger,
  onCartProductSetRefreshed
}) => {
  const state = { cartLogs: [] };
  cleanUpExpiredLocalStorageItems(logger);
  const logCartInteraction = (input) => {
    const date = /* @__PURE__ */ new Date();
    state.cartLogs.push({
      ...input,
      utcTimestamp: date.toISOString(),
      localTimestamp: date.toLocaleString()
    });
  };
  const logCartRequestResult = (input) => {
    const { cartId, message, errors, activity, metadata } = input;
    if (message) {
      logger.error(`ShoppingCart | Cart | ${activity} | ${message}`, {
        cartId,
        activity,
        module: "Cart"
      });
      return;
    }
    if (errors) {
      logger.error(`ShoppingCart | Cart | ${activity} | ${message}`, {
        cartId,
        activity,
        module: "Cart"
      });
      return;
    }
    if (!cartId) {
      logger.error("ShoppingCart | Cart | cartError | Cart is undefined", {
        cartId,
        module: "Cart",
        activity
      });
      return;
    }
    if (metadata) {
      logger.info(`ShoppingCart | Cart | ${activity} | Success`, {
        cartId,
        activity,
        module: "Cart",
        metadata
      });
      return;
    }
  };
  async function refreshCartProductSet(productSetId) {
    var _a, _b, _c, _d;
    if (!state.cartId) {
      logger.error(
        "ShoppingCart | Cart | refreshCartProductSet | Cart ID is not set, unable to refreshCartProductSet",
        {
          cartId: state.cartId,
          activity: "refreshCartProductSet",
          module: "Cart",
          productSetId
        }
      );
      return;
    }
    const { data, errors } = await apolloClient.mutate({
      mutation: RefreshCartProductSetDocument,
      variables: { input: { cartId: state.cartId, productSetId } }
    });
    logCartInteraction({
      activity: "refreshCartProductSet",
      cartId: state.cartId,
      cartState: ((_a = data == null ? void 0 : data.refreshCartProductSet) == null ? void 0 : _a.__typename) === "RefreshCartProductSetSuccessPayload" ? data.refreshCartProductSet.cart : void 0
    });
    if (!data) {
      logCartRequestResult({
        cartId: void 0,
        message: void 0,
        activity: "refreshCartProductSet",
        errors
      });
      return;
    }
    const successData = ((_b = data.refreshCartProductSet) == null ? void 0 : _b.__typename) === "RefreshCartProductSetSuccessPayload" ? data.refreshCartProductSet : void 0;
    const errorData = ((_c = data.refreshCartProductSet) == null ? void 0 : _c.__typename) === "CartNotFoundError" || ((_d = data.refreshCartProductSet) == null ? void 0 : _d.__typename) === "ProductSetNotFoundError" ? data.refreshCartProductSet : void 0;
    logCartRequestResult({
      cartId: successData == null ? void 0 : successData.cart.id,
      message: errorData == null ? void 0 : errorData.message,
      activity: "refreshCartProductSet",
      errors
    });
    onCartProductSetRefreshed({
      hasCartItemsBeenRemoved: (successData == null ? void 0 : successData.hasCartItemsBeenRemoved) ?? false,
      hasPriceChanged: (successData == null ? void 0 : successData.hasPriceChanged) || getPriceChangedFlagFromLocalStorage(state.cartId, logger)
    });
    if (successData == null ? void 0 : successData.hasPriceChanged) {
      setPriceChangedFlagInLocalStorage(state.cartId, logger);
    }
    return successData == null ? void 0 : successData.cart;
  }
  async function updateCartProductSet(productSetId) {
    var _a, _b;
    if (!state.cartId) {
      logger.error(
        "ShoppingCart | Cart | updateCartProductSet | Cart ID is not set, unable to updateCartProductSet",
        {
          cartId: state.cartId,
          activity: "updateCartProductSet",
          module: "Cart",
          productSetId
        }
      );
      return;
    }
    const { data, errors } = await apolloClient.mutate({
      mutation: UpdateCartProductSetDocument,
      variables: { input: { cartId: state.cartId, productSetId } }
    });
    const successData = ((_a = data == null ? void 0 : data.updateCartProductSet) == null ? void 0 : _a.__typename) === "UpdateCartProductSetSuccessPayload" ? data.updateCartProductSet : void 0;
    const errorData = ((_b = data == null ? void 0 : data.updateCartProductSet) == null ? void 0 : _b.__typename) === "UpdateCartProductSetErrorPayload" ? data.updateCartProductSet : void 0;
    logCartRequestResult({
      cartId: successData == null ? void 0 : successData.cart.id,
      message: errorData == null ? void 0 : errorData.message,
      activity: "updateCartProductSet",
      errors
    });
    logCartInteraction({
      activity: "updateCartProductSet",
      cartId: successData == null ? void 0 : successData.cart.id,
      cartState: successData == null ? void 0 : successData.cart
    });
    return successData == null ? void 0 : successData.cart;
  }
  const printCartInteractionLogs = () => {
    logger.error("ShoppingCart | Cart | printCartInteractionLogs", {
      cartId: state.cartId,
      activity: "printCartInteractionLogs",
      module: "Cart",
      metadata: state.cartLogs
    });
  };
  async function getCart(cachePolicy = "cache-only") {
    var _a, _b, _c;
    if (!state.cartId) {
      logger.error(
        "ShoppingCart | Cart | getCart | Cart ID is not set, unable to getCart",
        {
          cartId: state.cartId,
          activity: "getCart",
          module: "Cart"
        }
      );
      return;
    }
    const { data, errors } = await apolloClient.query({
      query: GetCartDocument,
      variables: { cartId: state.cartId },
      fetchPolicy: cachePolicy
    });
    const hasPriceChangedRecently = getPriceChangedFlagFromLocalStorage(
      state.cartId,
      logger
    );
    if (hasPriceChangedRecently) {
      onCartProductSetRefreshed({
        hasCartItemsBeenRemoved: false,
        hasPriceChanged: true
      });
    }
    if (errors || (data == null ? void 0 : data.cart)) {
      logCartRequestResult({
        cartId: (_a = data.cart) == null ? void 0 : _a.id,
        message: (_b = errors == null ? void 0 : errors[0]) == null ? void 0 : _b.message,
        activity: "getCart",
        errors,
        metadata: data.cart ? { isOrderReady: data.cart.isOrderReady.toString() } : void 0
      });
    }
    logCartInteraction({
      activity: "getCart",
      cartId: (_c = data == null ? void 0 : data.cart) == null ? void 0 : _c.id,
      cartState: data == null ? void 0 : data.cart
    });
    return (data == null ? void 0 : data.cart) ?? void 0;
  }
  return {
    setCartId: async (cartId) => {
      state.cartId = cartId;
      return getCart("cache-first");
    },
    getCartId() {
      return state.cartId;
    },
    getCart: async () => getCart(),
    updateCartProductSet: async (request) => {
      const { productSetId, reason } = request;
      if (!state.cartId) {
        logger.error(
          "ShoppingCart | Cart | updateCartProductSet | Cart ID is not set, unable to update productSet",
          {
            cartId: state.cartId,
            activity: "addLineItem",
            module: "Cart",
            request
          }
        );
        return;
      }
      if (reason === "JobInputChanged") {
        return await updateCartProductSet(productSetId);
      }
      if (reason === "ProductSetExpiry") {
        return await refreshCartProductSet(productSetId);
      }
      logCartRequestResult({
        cartId: state.cartId,
        activity: "updateCartProductSet",
        message: "ShoppingCart | Cart | updateCartProductSet | Invalid reason",
        errors: void 0
      });
      logCartInteraction({
        activity: "updateCartProductSet",
        cartId: state.cartId
      });
    },
    addCartItem: async (request) => {
      var _a, _b;
      if (!state.cartId) {
        logger.error(
          "ShoppingCart | Cart | addLineItem | Cart ID is not set, unable to add cart item",
          {
            cartId: state.cartId,
            activity: "addLineItem",
            module: "Cart",
            request
          }
        );
        return;
      }
      const { item } = request;
      const { data, errors } = await apolloClient.mutate(
        {
          mutation: AddItemToCartDocument,
          variables: { input: { cartId: state.cartId, item } }
        }
      );
      const successData = ((_a = data == null ? void 0 : data.addItemToCart) == null ? void 0 : _a.__typename) === "AddItemToCartSuccessPayload" ? data.addItemToCart : void 0;
      const errorData = ((_b = data == null ? void 0 : data.addItemToCart) == null ? void 0 : _b.__typename) === "AddItemToCartErrorPayload" ? data.addItemToCart : void 0;
      logCartRequestResult({
        cartId: successData == null ? void 0 : successData.cart.id,
        message: errorData == null ? void 0 : errorData.message,
        activity: "addCartItem",
        errors
      });
      logCartInteraction({
        cartId: successData == null ? void 0 : successData.cart.id,
        activity: "addCartItem",
        cartState: successData == null ? void 0 : successData.cart
      });
      return successData == null ? void 0 : successData.cart;
    },
    removeCartItem: async (request) => {
      var _a, _b;
      const { referenceId } = request;
      if (!state.cartId) {
        logger.error(
          "ShoppingCart | Cart | removeLineItem | Cart ID is not set, unable to remove cart item",
          {
            cartId: state.cartId,
            request,
            activity: "removeLineItem",
            module: "Cart"
          }
        );
        return;
      }
      const { data, errors } = await apolloClient.mutate({
        mutation: RemoveCartItemDocument,
        variables: { input: { cartId: state.cartId, referenceId } }
      });
      const successData = ((_a = data == null ? void 0 : data.removeCartItem) == null ? void 0 : _a.__typename) === "RemoveCartItemSuccessPayload" ? data.removeCartItem : void 0;
      const errorData = ((_b = data == null ? void 0 : data.removeCartItem) == null ? void 0 : _b.__typename) === "CartNotFoundError" ? data.removeCartItem : void 0;
      logCartRequestResult({
        cartId: successData == null ? void 0 : successData.cart.id,
        message: errorData == null ? void 0 : errorData.message,
        activity: "removeCartItem",
        errors
      });
      logCartInteraction({
        cartId: successData == null ? void 0 : successData.cart.id,
        activity: "removeCartItem",
        cartState: successData == null ? void 0 : successData.cart
      });
      return successData == null ? void 0 : successData.cart;
    },
    updateCartItem: async (request) => {
      var _a, _b;
      if (!state.cartId) {
        logger.error(
          "ShoppingCart | assignJobIdAndRevisionToParentProduct | Cart ID is not set, unable to assign cart item",
          {
            cartId: state.cartId,
            activity: "assignJobIdAndRevisionToParentProduct",
            module: "Cart",
            request
          }
        );
        return;
      }
      const { data, errors } = await apolloClient.mutate({
        mutation: UpdateJobForCartItemByReferenceDocument,
        variables: {
          input: {
            ...request,
            cartId: state.cartId
          }
        }
      });
      const successData = ((_a = data == null ? void 0 : data.updateJobForCartItemByReference) == null ? void 0 : _a.__typename) === "UpdateJobForCartItemByReferenceSuccessPayload" ? data.updateJobForCartItemByReference : void 0;
      const errorData = ((_b = data == null ? void 0 : data.updateJobForCartItemByReference) == null ? void 0 : _b.__typename) === "CartNotFoundError" ? data.updateJobForCartItemByReference : void 0;
      logCartRequestResult({
        cartId: successData == null ? void 0 : successData.cart.id,
        message: errorData == null ? void 0 : errorData.message,
        activity: "updateCartItem",
        errors,
        metadata: successData ? { isOrderReady: successData.cart.isOrderReady.toString() } : void 0
      });
      logCartInteraction({
        cartId: successData == null ? void 0 : successData.cart.id,
        cartState: successData == null ? void 0 : successData.cart,
        activity: "updateCartItem"
      });
      return successData == null ? void 0 : successData.cart;
    },
    updateCartPromoCode: async (request) => {
      var _a, _b;
      if (!state.cartId) {
        logger.error(
          "ShoppingCart | Cart | addPromoCode | Cart ID is not set, unable to add promo code",
          {
            cartId: state.cartId,
            request,
            module: "Cart",
            activity: "addPromoCode"
          }
        );
        return;
      }
      const { promoCode } = request;
      const { data, errors } = await apolloClient.mutate({
        mutation: UpdateCartPromoCodeDocument,
        variables: {
          input: {
            cartId: state.cartId,
            promoCode
          }
        }
      });
      const successData = ((_a = data == null ? void 0 : data.updateCartPromoCode) == null ? void 0 : _a.__typename) === "UpdateCartPromoCodeSuccessPayload" ? data.updateCartPromoCode : void 0;
      const errorData = ((_b = data == null ? void 0 : data.updateCartPromoCode) == null ? void 0 : _b.__typename) === "UpdateCartPromoCodeErrorPayload" ? data.updateCartPromoCode : void 0;
      logCartRequestResult({
        cartId: successData == null ? void 0 : successData.cart.id,
        message: errorData == null ? void 0 : errorData.message,
        activity: "updateCartPromoCode",
        errors
      });
      logCartInteraction({
        cartId: successData == null ? void 0 : successData.cart.id,
        cartState: successData == null ? void 0 : successData.cart,
        activity: "updateCartPromoCode"
      });
      return successData == null ? void 0 : successData.cart;
    },
    removeCartPromoCode: async () => {
      var _a, _b;
      if (!state.cartId) {
        logger.error(
          "ShoppingCart | Cart | removeCartPromoCode | Cart ID is not set, unable to remove promo code",
          {
            cartId: state.cartId,
            module: "Cart",
            activity: "removeCartPromoCode"
          }
        );
        return;
      }
      const { data, errors } = await apolloClient.mutate({
        mutation: RemoveCartPromoCodeDocument,
        variables: { input: { cartId: state.cartId } }
      });
      const successData = ((_a = data == null ? void 0 : data.removeCartPromoCode) == null ? void 0 : _a.__typename) === "RemoveCartPromoCodeSuccessPayload" ? data.removeCartPromoCode : void 0;
      const errorData = ((_b = data == null ? void 0 : data.removeCartPromoCode) == null ? void 0 : _b.__typename) === "CartNotFoundError" ? data.removeCartPromoCode : void 0;
      logCartRequestResult({
        cartId: successData == null ? void 0 : successData.cart.id,
        message: errorData == null ? void 0 : errorData.message,
        activity: "removeCartPromoCode",
        errors
      });
      logCartInteraction({
        cartId: successData == null ? void 0 : successData.cart.id,
        cartState: successData == null ? void 0 : successData.cart,
        activity: "removeCartPromoCode"
      });
      return successData == null ? void 0 : successData.cart;
    },
    printCartInteractionLogs
  };
};
const delay = () => new Promise(
  (resolve) => setTimeout(resolve, Math.floor(Math.random() * 1e3) + 200)
  // 200ms - 1.2s delay
);
const createMockCart = (_apolloClient, _logger) => {
  let cart = {
    id: "mock-cart-id",
    customerId: "example",
    isOrderReady: false,
    items: []
  };
  return {
    setCartId(_cartId) {
      return Promise.resolve(cart);
    },
    getCartId: () => cart.id,
    getCart: async () => {
      await delay();
      return cart;
    },
    updateCartProductSet: async (request) => {
      await delay();
      cart.productSetId = request.productSetId;
      return cart;
    },
    addCartItem: async (request) => {
      await delay();
      const items = (cart == null ? void 0 : cart.items) ?? [];
      const itemIndex = items.findIndex(
        (item) => item.referenceId === request.item.referenceId
      );
      const lineItem = {
        product: { id: request.item.product.id },
        parentReferenceId: request.item.parentReferenceId,
        referenceId: request.item.referenceId
      };
      if (itemIndex !== -1) {
        items[itemIndex] = lineItem;
      } else {
        items.push(lineItem);
      }
      cart = { ...cart, items };
      return cart;
    },
    removeCartItem: async (request) => {
      await delay();
      cart = {
        ...cart,
        items: [
          ...cart.items.filter(
            (item) => item.referenceId !== request.referenceId
          ) ?? []
        ]
      };
      return cart;
    },
    updateCartItem: async (request) => {
      var _a, _b, _c;
      await delay();
      const items = (cart == null ? void 0 : cart.items) ?? [];
      const itemIndex = items.findIndex(
        (item) => item.referenceId === request.referenceId
      );
      const lineItem = {
        product: {
          id: (_b = (_a = items[itemIndex]) == null ? void 0 : _a.product) == null ? void 0 : _b.id,
          job: {
            id: request.job.id,
            revision: request.job.revision
          }
        },
        parentReferenceId: (_c = items[itemIndex]) == null ? void 0 : _c.parentReferenceId,
        referenceId: request.referenceId
      };
      if (itemIndex !== -1) {
        items[itemIndex] = lineItem;
      }
      cart = { ...cart, items };
      return cart;
    },
    updateCartPromoCode: async (request) => {
      await delay();
      cart.promoCode = request.promoCode;
      return cart;
    },
    removeCartPromoCode: async () => {
      await delay();
      delete cart.promoCode;
      return cart;
    },
    printCartInteractionLogs: () => {
    }
  };
};
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  if (!getRandomValues) {
    getRandomValues = typeof crypto !== "undefined" && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);
    if (!getRandomValues) {
      throw new Error("crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported");
    }
  }
  return getRandomValues(rnds8);
}
const byteToHex = [];
for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 256).toString(16).slice(1));
}
function unsafeStringify(arr, offset = 0) {
  return byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + "-" + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + "-" + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + "-" + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + "-" + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]];
}
const randomUUID = typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID.bind(crypto);
const native = {
  randomUUID
};
function v4(options, buf, offset) {
  if (native.randomUUID && !buf && !options) {
    return native.randomUUID();
  }
  options = options || {};
  const rnds = options.random || (options.rng || rng)();
  rnds[6] = rnds[6] & 15 | 64;
  rnds[8] = rnds[8] & 63 | 128;
  return unsafeStringify(rnds);
}
const getCreateOrderResultFromData = (data) => {
  var _a, _b;
  if (((_a = data == null ? void 0 : data.createOrderWithPromotion) == null ? void 0 : _a.__typename) === "CreateOrderSuccessPayload" && (data == null ? void 0 : data.createOrderWithPromotion.order)) {
    return {
      result: "success",
      orderId: data.createOrderWithPromotion.order.orderId,
      orderNumber: data.createOrderWithPromotion.order.orderNumber
    };
  } else if (((_b = data == null ? void 0 : data.createOrderWithPromotion) == null ? void 0 : _b.__typename) === "PromotionInvalidError") {
    return {
      result: "failure",
      error: {
        type: "PromoCodeTypeError",
        message: data.createOrderWithPromotion.message
      }
    };
  }
};
const getCreateOrderResultFromErrors = (errors) => {
  var _a;
  const error = errors == null ? void 0 : errors[0];
  if (error === void 0) {
    return;
  }
  switch ((_a = error.extensions) == null ? void 0 : _a.code) {
    case "PRODUCT_SET_EXPIRED":
    case "PRODUCT_SET_NOT_FOUND": {
      return {
        result: "failure",
        error: {
          ...error,
          type: "ProductTypeError"
        }
      };
    }
    default:
      return {
        result: "failure",
        error: {
          ...error,
          type: "OtherTypeError"
        }
      };
  }
};
const toCreateOrderResult = ({
  data,
  errors
}) => {
  const resultFromErrors = getCreateOrderResultFromErrors(errors);
  const resultFromData = getCreateOrderResultFromData(data);
  if (resultFromData) {
    return resultFromData;
  }
  if (resultFromErrors) {
    return resultFromErrors;
  }
};
const isAdProduct = (cartItemProduct) => cartItemProduct.__typename === "AdProductCartItem" || cartItemProduct.__typename === "AdProductAddOnCartItem" || cartItemProduct.__typename === "AdProductUpgradeCartItem";
const mapCartLineItemToOrderLineItem = (cartItem, productSetId) => {
  var _a, _b;
  return {
    productAttributes: {
      catalogueProduct: {
        productId: cartItem.product.id,
        productSetId,
        jobId: isAdProduct(cartItem.product) ? (_a = cartItem.product.job) == null ? void 0 : _a.id : void 0,
        revisionId: isAdProduct(cartItem.product) ? (_b = cartItem.product.job) == null ? void 0 : _b.revision : void 0
      }
    },
    productType: "CATALOGUE_PRODUCT"
  };
};
const createOrderModule = ({
  apolloClient,
  logger,
  createOrderMaxRetries,
  onOrderUpdated,
  onPromoCodeError,
  onProductError,
  getCart
}) => {
  const getCreateOrderRequest = async (cart) => {
    if (!(cart == null ? void 0 : cart.isOrderReady)) {
      logger.warn(`ShoppingCart | Order | createOrderError | Cart not ready`, {
        cartId: cart.id,
        activity: "createOrder",
        module: "ShoppingCart"
      });
      return;
    }
    return {
      lineItems: cart.items,
      productSetId: cart.productSetId,
      promoCode: cart.promoCode ?? void 0
    };
  };
  const createOrder = async (request) => {
    const { data, errors } = await apolloClient.mutate({
      mutation: CreateOrderWithPromotionDocument,
      variables: {
        input: {
          orderCreationId: v4(),
          lineItems: request.lineItems.map(
            (item) => mapCartLineItemToOrderLineItem(item, request.productSetId)
          ),
          promoCode: request.promoCode
        }
      },
      errorPolicy: "all"
    });
    return toCreateOrderResult({ data, errors });
  };
  const createOrderHandler = async (retry = 0) => {
    const cart = await getCart();
    if (!cart) {
      logger.warn("ShoppingCart | Order | createOrderError | No cart", {
        activity: "createOrderHandler",
        module: "ShoppingCart"
      });
      return;
    }
    const createdOrderRequest = await getCreateOrderRequest(cart);
    if (!createdOrderRequest) {
      return;
    }
    const response = await createOrder(createdOrderRequest);
    if (response === void 0) {
      logger.warn("ShoppingCart | Order | createOrderError | No response", {
        cartId: cart.id,
        activity: "createOrderHandler",
        module: "ShoppingCart"
      });
      onOrderUpdated({
        order: void 0,
        loading: true
      });
      return;
    }
    if (response.result === "success") {
      onOrderUpdated({
        order: {
          orderId: response.orderId,
          orderNumber: response.orderNumber
        },
        loading: false
      });
      return;
    }
    if (response.error.type === "PromoCodeTypeError") {
      await onPromoCodeError();
    }
    if (response.error.type === "ProductTypeError" && retry < createOrderMaxRetries) {
      await onProductError();
      return await createOrderHandler(retry + 1);
    }
    if (response.error.type === "OtherTypeError") {
      logger.error("ShoppingCart | Order | createOrderError | OtherTypeError", {
        cartId: cart.id,
        activity: "createOrder",
        module: "ShoppingCart",
        createOrderError: response.error
      });
      return onOrderUpdated({
        order: void 0,
        loading: false,
        createOrderFatalError: response.error.message
      });
    }
  };
  return {
    createOrderHandler
  };
};
class BadRequestError extends Error {
  constructor(message, metadata) {
    super(`Failed to request: ${message}`);
    __publicField(this, "errorCode", "BAD_REQUEST_ERROR");
    __publicField(this, "metadata", {});
    this.name = "BadRequestError";
    if (metadata) {
      this.metadata = metadata;
    }
  }
}
class UnrecoverableError extends Error {
  constructor(message, metadata) {
    super(`Failed to recover: ${message}`);
    __publicField(this, "errorCode", "UNRECOVERABLE_ERROR");
    __publicField(this, "metadata", {});
    this.name = "UnrecoverableError";
    if (metadata) {
      this.metadata = metadata;
    }
  }
}
const createProductSet = (client, spec, abortController) => async (data) => {
  var _a;
  const { signal } = abortController;
  const response = await client.mutate({
    mutation: spec.documentNode,
    variables: spec.getVariables(data),
    context: {
      ...client.defaultContext,
      fetchOptions: {
        signal
      }
    }
  });
  if (!response || !response.data) {
    throw new UnrecoverableError(
      "No data returned from createProductSet mutation",
      { errors: response == null ? void 0 : response.errors, data }
    );
  }
  return (_a = response.data) == null ? void 0 : _a[spec.responseKey].productSet;
};
const CREATE_PRODUCT_STRATEGIES = {
  CreateProductSetForAdPosting: {
    responseKey: "createProductSetForAdPosting",
    documentNode: CreateProductSetForAdPostingDocument,
    getVariables: (data) => ({
      input: {
        advertiserNation: data.advertiserNation,
        draftId: data.draftId,
        jobAdDetails: {
          locationId: data.locationId,
          roleTitle: data.roleTitle,
          subClassificationId: data.subClassificationId
        },
        locale: data.locale,
        userIntent: data.userIntent
      }
    }),
    getShouldSkip: (data) => {
      const hasAllData = Boolean(
        data && data.roleTitle && data.locationId && data.subClassificationId
      );
      return !hasAllData;
    }
  },
  CreateProductSetForAdUpgrade: {
    responseKey: "createProductSetForAdUpgradePurchase",
    documentNode: CreateProductSetForAdUpgradePurchaseDocument,
    getVariables: (data) => ({
      input: {
        jobId: data.jobId,
        locale: data.locale,
        opportunityId: data.opportunityId,
        userIntent: data.userIntent
      }
    }),
    getShouldSkip: (data) => {
      const hasAllData = Boolean(data && data.jobId);
      return !hasAllData;
    }
  },
  CreateProductSetForMultiLocation: {
    responseKey: "createProductSetForMultiLocation",
    documentNode: CreateProductSetForMultiLocationDocument,
    getVariables: (data) => ({
      input: {
        draftId: data.draftId,
        jobAdDetails: {
          locationId: data.locationId,
          roleTitle: data.roleTitle,
          subClassificationId: data.subClassificationId
        },
        jobId: data.jobId,
        locale: data.locale
      }
    }),
    getShouldSkip: (data) => {
      const hasAllData = Boolean(
        data && data.roleTitle && data.locationId && data.subClassificationId && data.jobId
      );
      return !hasAllData;
    }
  }
};
const getCreateProductSetStrategy = (createProductSetId) => {
  const strategy = CREATE_PRODUCT_STRATEGIES[createProductSetId];
  if (!strategy) {
    throw new BadRequestError(
      "getCreateProductSetStrategy: Invalid product set ID"
    );
  }
  return strategy;
};
class ProductSetError extends Error {
  constructor(message) {
    super(`Product set error: ${message}`);
    __publicField(this, "errorCode", "PRODUCT_SET_ERROR");
    this.name = "ProductSetError";
  }
}
class ProductSetExpiredError extends Error {
  constructor(message) {
    super(`Product set error: ${message}`);
    __publicField(this, "errorCode", "PRODUCT_SET_EXPIRED_ERROR");
    this.name = "ProductSetExpiredError";
  }
}
const isValidProductSetQueryResponse = (key, data) => data.hasOwnProperty(key);
const isProductSetResponseInvalid = (products) => {
  if (products && (products.__typename === "ProductSetExpiredError" || products.__typename === "ProductSetNotFoundError")) {
    return true;
  }
  return false;
};
const isProductSetResponseExpired = (products) => {
  if (products && products.__typename === "ProductSetExpiredError") {
    return true;
  }
  return false;
};
const isProductSetExpiredError = (error) => {
  if (typeof error === "object" && error !== null && "name" in error) {
    return error.name === "ProductSetExpiredError";
  }
  return false;
};
const isProductSetError = (error) => {
  if (typeof error === "object" && error !== null && "name" in error) {
    return error.name === "ProductSetError";
  }
  return false;
};
const isBadRequestError = (error) => {
  if (typeof error === "object" && error !== null && "name" in error) {
    return error.name === "BadRequestError";
  }
  return false;
};
const isAbortError = (error) => {
  var _a, _b;
  if (error instanceof ApolloError && error.networkError !== null && "message" in error.networkError) {
    return ((_a = error.networkError) == null ? void 0 : _a.message) === "signal is aborted without reason" || ((_b = error.networkError) == null ? void 0 : _b.name) === "AbortError";
  }
  if (error instanceof DOMException) {
    return error.name === "AbortError" || error.message === "signal is aborted without reason";
  }
  return false;
};
const SKIPPED_RESPONSE = {
  loading: true,
  error: void 0,
  data: void 0
};
const getProductSet = async ({
  productSetId,
  fieldName,
  query
}) => {
  const skip = !productSetId;
  if (skip) {
    return SKIPPED_RESPONSE;
  }
  const { loading, error, data } = await query(productSetId);
  if (!isValidProductSetQueryResponse(fieldName, data)) {
    throw new BadRequestError("Bad request to get product set", {
      productSetId,
      fieldName
    });
  }
  const productsResponse = data[fieldName];
  if (isProductSetResponseInvalid(productsResponse)) {
    throw new ProductSetError(productsResponse.message);
  }
  return {
    loading,
    data: productsResponse,
    error
  };
};
const getProductSetFeatures = (apolloClient) => async ({
  productSetId
}) => {
  const { data, error, loading } = await apolloClient.query({
    query: GetProductSetItemsWithFeaturesOnlyDocument,
    variables: { input: { productSetId } }
  });
  if (isProductSetResponseExpired(data.getProductSet)) {
    throw new ProductSetExpiredError("Product set expired");
  }
  if (isProductSetResponseInvalid(data.getProductSet)) {
    throw new ProductSetError("Failed to get product set");
  }
  return {
    data,
    error,
    loading
  };
};
const createProduct = ({
  apolloClient,
  createProductSetId,
  logger,
  onCreateProductSetId,
  onCreateProductSetError
}) => {
  const spec = getCreateProductSetStrategy(createProductSetId);
  const memoizedCreateProductSet = memoize(() => {
    let currentController = null;
    return async (data) => {
      if (currentController) {
        currentController.abort();
      }
      currentController = new AbortController();
      return await createProductSet(
        apolloClient,
        spec,
        currentController
      )(data);
    };
  });
  const createProductSetRequest = async (data, reason) => {
    if (!spec) {
      throw new BadRequestError(
        `invalid createProductSetId: ${createProductSetId}`
      );
    }
    if (spec.getShouldSkip(data)) {
      logger.info("Skipping createProductSet due to missing request data", {
        data,
        activity: "createProductSet",
        module: "Product"
      });
      return;
    }
    try {
      const productSet = await memoizedCreateProductSet()(data);
      await onCreateProductSetId(productSet.id, reason);
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }
      onCreateProductSetError(
        new UnrecoverableError("Failed to create product set", { error })
      );
    }
  };
  const debouncedCreateProductSet = debounce(createProductSetRequest, 200);
  return {
    // Creates a new product set using the injected method from the host application
    createProductSet: async (data, reason) => {
      await createProductSetRequest(data, reason);
    },
    // Creates a new product set using the injected method from the host application
    debouncedCreateProductSet: async (data, reason) => {
      await debouncedCreateProductSet(data, reason);
    },
    // Loads the display based products for a given product set id
    getProductSet: async ({
      productSetId,
      fieldName,
      query
    }) => {
      try {
        return await getProductSet({
          productSetId,
          fieldName,
          query
        });
      } catch (error) {
        if (isProductSetError(error)) {
          throw error;
        }
        if (isBadRequestError(error)) {
          return {
            loading: false,
            error: new BadRequestError("Bad request to get product set", {
              error,
              productSetId
            }),
            data: void 0
          };
        }
        return {
          loading: false,
          error: new UnrecoverableError("Failed to get product set", {
            error,
            productSetId
          }),
          data: void 0
        };
      }
    },
    // loads the product set items which is currently used to get productFeatures
    getProductSetFeatures: async ({
      productSetId
    }) => {
      try {
        return await getProductSetFeatures(apolloClient)({ productSetId });
      } catch (error) {
        if (isProductSetError(error) || isProductSetExpiredError(error)) {
          throw error;
        }
        return {
          loading: false,
          error: new UnrecoverableError(
            "Failed to get product set when getting features",
            { productSetId, error }
          ),
          data: void 0
        };
      }
    }
  };
};
const createProductFeatures = ({
  getProductSetFeatures: getProductSetFeatures2,
  onGetProductSetFeaturesError,
  onGetProductSetFeatures
}) => {
  let prevProductSetId = "";
  return {
    getProductFeatures: async (productSetId) => {
      if (prevProductSetId === productSetId) return;
      prevProductSetId = productSetId;
      const productFeatures = /* @__PURE__ */ new Map();
      try {
        const { data, error } = await getProductSetFeatures2({
          productSetId
        });
        if (error) {
          onGetProductSetFeaturesError(error);
          return;
        }
        if (data && data.getProductSet.__typename === "ProductSet") {
          data.getProductSet.items.forEach((item) => {
            if (!(item == null ? void 0 : item.features) || (item == null ? void 0 : item.features) === null) return;
            const features = item.features.filter(
              (feature) => feature !== null
            );
            productFeatures.set(item.id, {
              features
            });
          });
        }
        onGetProductSetFeatures(productFeatures);
      } catch (error) {
        if (isProductSetExpiredError(error)) {
          return;
        }
        if (error instanceof Error) {
          onGetProductSetFeaturesError(error);
        }
      }
    }
  };
};
const ROOT_PRODUCT_REFERENCE_ID = "product|parent";
const ROOT_BUDGET_REFERENCE_ID = "budget|parent";
const getReferenceIdsForProduct = (createProductSetId) => (productId, productType) => {
  switch (createProductSetId) {
    case "CreateProductSetForAdPosting":
    case "CreateProductSetForAdUpgrade":
    case "CreateProductSetForMultiLocation":
    default: {
      if (productType === "AdProductAddOn") {
        return {
          referenceId: `addon|${productId}`,
          parentReferenceId: ROOT_PRODUCT_REFERENCE_ID
        };
      }
      if (productType === "AdBudgetProduct") {
        return {
          referenceId: ROOT_BUDGET_REFERENCE_ID,
          parentReferenceId: ROOT_PRODUCT_REFERENCE_ID
        };
      }
      return {
        referenceId: ROOT_PRODUCT_REFERENCE_ID,
        parentReferenceId: void 0
      };
    }
  }
};
const createSubscriberStore = ({
  onProductsStateChanged,
  getProductSet: getProductSet2,
  onProductSetExpired
}) => {
  const state = {
    productSetId: null,
    subscribers: /* @__PURE__ */ new Map()
  };
  const runSubscriptionBySubscriptionKey = async (key) => {
    if (!state.productSetId) return;
    const config = state.subscribers.get(key);
    if (!config) return;
    onProductsStateChanged({
      subscriberKey: key,
      error: void 0,
      loading: true
    });
    try {
      const { loading, error, data } = await getProductSet2({
        productSetId: state.productSetId,
        query: config.operation,
        fieldName: config.fieldName
      });
      onProductsStateChanged({
        subscriberKey: key,
        error,
        loading,
        products: data,
        display: data == null ? void 0 : data.display
      });
    } catch (error) {
      if (isProductSetError(error)) {
        onProductSetExpired();
      }
    }
  };
  const subscribeForProducts = (key, fieldName, operation) => {
    state.subscribers.set(key, { fieldName, operation });
    runSubscriptionBySubscriptionKey(key);
  };
  const unsubscribeForProducts = (id) => {
    state.subscribers.delete(id);
  };
  const onProductSetIdUpdated = (productSetId) => {
    state.productSetId = productSetId;
    Array.from(state.subscribers.entries()).map(
      async ([key]) => await runSubscriptionBySubscriptionKey(key)
    );
  };
  return {
    subscribeForProducts,
    unsubscribeForProducts,
    onProductSetIdUpdated
  };
};
const createShoppingCart = ({
  apolloClient,
  createProductSetId,
  onProductsStateChanged,
  onProductSetIdUpdated,
  onProductSetFeaturesUpdated,
  onOrderUpdated,
  onCartProductCartProgress,
  setCartId,
  useMockCart,
  logger
}) => {
  const state = {
    subscribersForProducts: /* @__PURE__ */ new Map(),
    inputDataForProductSetCreation: {}
  };
  const cart = useMockCart ? createMockCart() : createCart({
    apolloClient,
    logger,
    onCartProductSetRefreshed: ({
      hasCartItemsBeenRemoved,
      hasPriceChanged
    }) => {
      if (hasCartItemsBeenRemoved) {
        onOrderUpdated({ productAlert: "SELECTED_PRODUCT_UNAVAILABLE" });
      }
      if (hasPriceChanged) {
        onOrderUpdated({ productAlert: "SELECTED_PRODUCT_PRICE_CHANGED" });
      }
    }
  });
  const CREATE_ORDER_MAX_RETRIES = 1;
  const removePromoCode = () => cart.removeCartPromoCode();
  const order = createOrderModule({
    apolloClient,
    logger,
    createOrderMaxRetries: CREATE_ORDER_MAX_RETRIES,
    onOrderUpdated,
    onPromoCodeError: removePromoCode,
    onProductError: () => product.createProductSet(
      state.inputDataForProductSetCreation,
      "ProductSetExpiry"
    ),
    getCart: cart.getCart
  });
  const product = createProduct({
    apolloClient,
    createProductSetId,
    logger,
    onCreateProductSetId: async (productSetId, reason) => {
      await cart.updateCartProductSet({
        productSetId,
        reason
      });
      subscribers.onProductSetIdUpdated(productSetId);
      onProductSetIdUpdated(productSetId);
      getProductFeatures(productSetId);
    },
    onCreateProductSetError: (error) => {
      logger.error(
        "ShoppingCart | Product | createProductSetError | Error creating product set",
        {
          cartId: cart.getCartId(),
          activity: "createProductSet",
          module: "ShoppingCart",
          error
        }
      );
    }
  });
  const { getProductFeatures } = createProductFeatures({
    getProductSetFeatures: product.getProductSetFeatures,
    onGetProductSetFeaturesError: (error) => {
      logger.error(
        "ShoppingCart | ProductFeatures | getProductSetFeatures | Error getting product set features",
        {
          cartId: cart.getCartId(),
          activity: "getProductSetFeatures",
          module: "ShoppingCart",
          error
        }
      );
    },
    onGetProductSetFeatures: onProductSetFeaturesUpdated
  });
  const subscribers = createSubscriberStore({
    onProductsStateChanged,
    getProductSet: product.getProductSet,
    onProductSetExpired: () => {
      product.debouncedCreateProductSet(
        state.inputDataForProductSetCreation,
        "ProductSetExpiry"
      );
    }
  });
  const updateInputDataForProductSetCreation = (value) => {
    state.inputDataForProductSetCreation = value;
    product.debouncedCreateProductSet(
      state.inputDataForProductSetCreation,
      "JobInputChanged"
    );
  };
  const mapProductTypeToCartProductType = (productType) => {
    switch (productType) {
      case "AdProduct":
        return "AD_PRODUCT";
      case "AdProductUpgrade":
        return "AD_PRODUCT_UPGRADE";
      case "AdBudgetProduct":
        return "AD_BUDGET";
      case "AdProductAddOn":
        return "AD_PRODUCT_ADD_ON";
      default:
        throw new Error(`Unknown product type: ${productType}`);
    }
  };
  return {
    addLineItem: async ({ productId, productType }) => {
      const { referenceId, parentReferenceId } = getReferenceIdsForProduct(
        createProductSetId
      )(productId, productType);
      onCartProductCartProgress("start", productId);
      const cartItemProductType = mapProductTypeToCartProductType(productType);
      await cart.addCartItem({
        item: {
          product: { id: productId, productType: cartItemProductType },
          parentReferenceId,
          referenceId
        }
      });
      onCartProductCartProgress("finish", productId);
    },
    removeLineItem: async ({ productId, productType }) => {
      const { referenceId } = getReferenceIdsForProduct(createProductSetId)(
        productId,
        productType
      );
      onCartProductCartProgress("start", productId);
      await cart.removeCartItem({
        referenceId
      });
      onCartProductCartProgress("finish", productId);
    },
    assignJobIdAndRevisionToParentProduct: async ({ jobId, revisionId }) => {
      const referenceId = ROOT_PRODUCT_REFERENCE_ID;
      await cart.updateCartItem({
        job: { id: jobId, revision: revisionId },
        referenceId
      });
    },
    addPromoCode: async (promoCode) => cart.updateCartPromoCode({
      promoCode
    }),
    removePromoCode,
    setCartId: async (cartId) => {
      const cartResult = await cart.setCartId(cartId);
      setCartId(cartId);
      if (cartResult == null ? void 0 : cartResult.productSetId) {
        subscribers.onProductSetIdUpdated(cartResult.productSetId);
        getProductFeatures(cartResult.productSetId);
      }
    },
    createOrder: async () => {
      const cartResult = await cart.getCart();
      if (!cartResult) {
        logger.error(
          "ShoppingCart | createOrder | Error creating order, cart not found",
          {
            cartId: cart.getCartId(),
            activity: "createOrder",
            module: "ShoppingCart"
          }
        );
        return;
      }
      await order.createOrderHandler();
    },
    setInputDataForProductSetCreation: (value) => {
      state.inputDataForProductSetCreation = value;
    },
    updateInputDataForProductSetCreation,
    printCartInteractionLogs: cart.printCartInteractionLogs,
    subscribeForProducts: subscribers.subscribeForProducts,
    unsubscribeForProducts: subscribers.unsubscribeForProducts
  };
};
const getUniqueCartFeatures = (cartState, productFeatures) => {
  if (!(cartState == null ? void 0 : cartState.items) || cartState.items.length === 0 || !productFeatures) {
    return [];
  }
  const features = /* @__PURE__ */ new Set();
  for (const item of cartState.items) {
    const product = productFeatures.get(item.product.id);
    if (product == null ? void 0 : product.features) {
      for (const feature of product.features) {
        if (feature) {
          features.add([feature]);
        }
      }
    }
  }
  return Array.from(features).flat();
};
const defaultLogger = {
  /* eslint-disable no-console */
  warn: console.warn,
  error: console.error,
  info: console.info
  /* eslint-enable no-console */
};
const ShoppingCartContext = createContext({
  productResponses: {},
  cartState: void 0,
  cartFeatures: [],
  orderState: {
    order: void 0,
    loading: false,
    productAlert: void 0,
    createOrderFatalError: void 0
  },
  cartProductInProgress: {},
  productSetId: void 0,
  onApplyPromoCode: () => {
  },
  onClearPromoCode: () => {
  },
  addLineItem: () => {
  },
  removeLineItem: () => {
  },
  updateInputData: () => {
  },
  subscribeForProducts: () => {
  },
  unsubscribeForProducts: () => {
  },
  setCartId: () => {
  },
  setInputData: () => {
  },
  createOrder: () => {
  },
  assignJobIdAndRevisionToParentProduct: () => {
  },
  printCartInteractionLogs: () => {
  }
});
const ShoppingCartProvider = ({
  children,
  createProductSetId,
  logger = defaultLogger,
  useMockCart = false
}) => {
  var _a;
  const [searchParams] = useSearchParams();
  const [productSetFeatures, setProductSetFeatures] = useState(/* @__PURE__ */ new Map());
  const initialOrderState = {
    order: void 0,
    loading: false,
    productAlert: void 0,
    createOrderFatalError: void 0
  };
  const [cartId, setCartId] = useState();
  const [orderState, setOrderState] = useState(initialOrderState);
  const [productResponses, setProductResponses] = useState({});
  const [cartProductInProgress, setCartProductInProgress] = useState({});
  const [productSetId, setProductSetId] = useState();
  const client = useApolloClient();
  const shoppingCart = useMemo(
    () => createShoppingCart({
      apolloClient: client,
      useMockCart,
      setCartId,
      createProductSetId,
      logger,
      onProductSetIdUpdated: (newProductSetId) => {
        setOrderState(initialOrderState);
        setProductSetId(newProductSetId);
      },
      onProductsStateChanged: ({ subscriberKey, ...data }) => {
        setProductResponses((current) => {
          var _a2, _b;
          return {
            ...current,
            [subscriberKey]: {
              loading: data.loading,
              error: data.error,
              // Maintain existing values while loading new ones
              products: data.products ?? (data.loading ? (_a2 = current[subscriberKey]) == null ? void 0 : _a2.products : data.products),
              display: data.display ?? (data.loading ? (_b = current[subscriberKey]) == null ? void 0 : _b.display : data.display)
            }
          };
        });
      },
      onProductSetFeaturesUpdated: (newProductSetFeatures) => {
        setProductSetFeatures(newProductSetFeatures);
      },
      onCartProductCartProgress: (progress, productId) => {
        setCartProductInProgress((current) => {
          switch (progress) {
            case "start": {
              return { ...current, [productId]: true };
            }
            case "finish": {
              const newState = { ...current };
              delete newState[productId];
              return newState;
            }
            default:
              return current;
          }
        });
      },
      onOrderUpdated: (newOrderState) => setOrderState((currentState) => ({
        ...currentState,
        ...newOrderState
      }))
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, createProductSetId, useMockCart]
  );
  useEffect(() => {
    const cache = client.cache;
    cache.policies.addTypePolicies({
      Query: {
        fields: {
          cart: {
            read(_, { args, toReference }) {
              return toReference({
                __typename: "Cart",
                id: args == null ? void 0 : args.cartId
              });
            }
          }
        }
      }
    });
  }, [client]);
  const { data: cartData } = useGetCartQuery({
    variables: { cartId },
    skip: !cartId,
    fetchPolicy: "cache-only"
  });
  const cartFeatures = getUniqueCartFeatures(
    (cartData == null ? void 0 : cartData.cart) ?? void 0,
    productSetFeatures
  );
  useEffect(() => {
    var _a2, _b;
    if (createProductSetId !== "CreateProductSetForAdPosting") return;
    const hasPromoCode = (_a2 = cartData == null ? void 0 : cartData.cart) == null ? void 0 : _a2.promoCode;
    const hasBudgetProduct = (_b = cartData == null ? void 0 : cartData.cart) == null ? void 0 : _b.items.some(
      (item) => item.product.__typename === "AdBudgetCartItem"
    );
    if (hasPromoCode && hasBudgetProduct) {
      shoppingCart.removeLineItem({
        productId: "",
        productType: "AdBudgetProduct"
      });
    }
  }, [(_a = cartData == null ? void 0 : cartData.cart) == null ? void 0 : _a.promoCode]);
  useEffect(() => {
    const orderId = searchParams.get("orderid");
    if (orderId) {
      setOrderState((currentState) => ({
        ...currentState,
        order: {
          orderId,
          orderNumber: ""
          // TODO: can we remove this?
        }
      }));
    }
  }, [searchParams]);
  return /* @__PURE__ */ jsx(
    ShoppingCartContext.Provider,
    {
      value: {
        productResponses,
        cartState: (cartData == null ? void 0 : cartData.cart) ?? void 0,
        cartFeatures,
        orderState,
        cartProductInProgress,
        productSetId,
        addLineItem: shoppingCart.addLineItem,
        removeLineItem: shoppingCart.removeLineItem,
        updateInputData: shoppingCart.updateInputDataForProductSetCreation,
        subscribeForProducts: shoppingCart.subscribeForProducts,
        unsubscribeForProducts: shoppingCart.unsubscribeForProducts,
        onApplyPromoCode: shoppingCart.addPromoCode,
        onClearPromoCode: shoppingCart.removePromoCode,
        setCartId: shoppingCart.setCartId,
        setInputData: shoppingCart.setInputDataForProductSetCreation,
        createOrder: shoppingCart.createOrder,
        printCartInteractionLogs: shoppingCart.printCartInteractionLogs,
        assignJobIdAndRevisionToParentProduct: shoppingCart.assignJobIdAndRevisionToParentProduct
      },
      children
    }
  );
};
export {
  ShoppingCartContext,
  ShoppingCartProvider
};
